// admin/pages/Sfx.tsx — SFX backlog / tag-frequency tracker.
//
// Renders every sound tag the AI has ever emitted, split into two tables:
//
//   1. Backlog (unknown tags) — tags the model has emitted but the bundled
//      catalogue.ts doesn't have an entry for. This is the headline list:
//      the tags ordered by frequency tell you which assets are worth
//      sourcing next. Higher visual weight.
//
//   2. Known tags (already curated) — tags that DO appear in
//      catalogue.ts. Confirms what's playing in the wild. Lower visual
//      weight.
//
// Data flow: on mount, dispatch loading and emit `audio:tag_stats_query`.
// Live `sfx_tag_emitted` events flow into the same slice via the reducer
// bridge so the page updates without a refresh.
//
// Cross-reference: AMBIENT_CATALOGUE / SPORADIC_CATALOGUE / ONESHOT_CATALOGUE
// imported directly from web/src/audio/catalogue.ts. A tag is "known" if its
// category's catalogue object has a matching key. The catalogue ships with
// most entries commented out today, so during Phase A almost everything
// lands in the Backlog table — that's the whole point.
//
// View-only — no curation actions yet (Phase C). No row mutations, no
// inline edits.

import {
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type RefObject,
} from "react";
import type { Socket } from "socket.io-client";
import { styles, tokens } from "../styles";
import { sfxActions, useAppDispatch, useAppSelector } from "../store";
import { RpcError } from "../socket/rpc";
import type {
  AdminSfxTagStat,
  AdminTagStatsQueryAck,
  SfxCategory,
} from "../store/types";
import {
  AMBIENT_CATALOGUE,
  SPORADIC_CATALOGUE,
  ONESHOT_CATALOGUE,
} from "../../audio/catalogue";
import { SCENARIO_PRESENTATIONS } from "../../scenarios/presentation";

interface SfxPageProps {
  socket: RefObject<Socket | null>;
}

const QUERY_TIMEOUT_MS = 15_000;
const PAGE_LIMIT = 200;

export default function Sfx({ socket }: SfxPageProps) {
  const dispatch = useAppDispatch();
  const stats = useAppSelector((s) => s.sfx.stats);
  const loaded = useAppSelector((s) => s.sfx.loaded);
  const loading = useAppSelector((s) => s.sfx.loading);
  const error = useAppSelector((s) => s.sfx.error);

  // Run the initial query once per mount when we haven't already loaded.
  // Live broadcasts keep the slice fresh after that; no refetch needed
  // unless the user navigates away and back AFTER signing out + in.
  //
  // `loading` is intentionally NOT in the dep array: dispatching
  // sfxStatsLoading(true) below flips it, and if the dep array tracked it
  // the effect would re-run, its cleanup would set cancelled=true on the
  // first run, and the eventual sfxStatsLoaded dispatch would be silently
  // discarded — page stuck on "Loading…" forever. The `loading` check in
  // the guard below is a belt-and-braces guard against rapid remount.
  useEffect(() => {
    if (loaded || loading) return;
    const sock = socket.current;
    if (!sock) return;
    let cancelled = false;
    dispatch(sfxActions.sfxStatsLoading(true));
    (async () => {
      try {
        const result = await new Promise<AdminSfxTagStat[]>((resolve, reject) => {
          if (!sock.connected) {
            reject(new RpcError("socket_not_connected"));
            return;
          }
          let settled = false;
          const timeout = window.setTimeout(() => {
            if (settled) return;
            settled = true;
            reject(new RpcError("timeout"));
          }, QUERY_TIMEOUT_MS);
          sock.emit(
            "audio:tag_stats_query",
            { limit: PAGE_LIMIT },
            (ack: unknown) => {
              if (settled) return;
              settled = true;
              window.clearTimeout(timeout);
              if (
                ack &&
                typeof ack === "object" &&
                (ack as { ok: boolean }).ok
              ) {
                const a = ack as AdminTagStatsQueryAck;
                if (a.ok) resolve(a.stats);
                else reject(new RpcError(a.error));
              } else {
                const e =
                  (ack as { error?: string })?.error ?? "unknown_error";
                reject(new RpcError(e));
              }
            },
          );
        });
        if (cancelled) return;
        dispatch(sfxActions.sfxStatsLoaded(result));
      } catch (err) {
        if (cancelled) return;
        dispatch(
          sfxActions.sfxStatsError(
            err instanceof Error ? err.message : String(err),
          ),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, dispatch, loaded]);

  // Split + sort. Tags whose category-matching catalogue has the key are
  // "known"; everything else is backlog. Sort each list desc by count.
  const { backlog, known } = useMemo(() => {
    const knownList: AdminSfxTagStat[] = [];
    const backlogList: AdminSfxTagStat[] = [];
    for (const s of stats) {
      if (isTagKnown(s.tag, s.category)) {
        knownList.push(s);
      } else {
        backlogList.push(s);
      }
    }
    const byCountDesc = (a: AdminSfxTagStat, b: AdminSfxTagStat) =>
      b.count - a.count;
    knownList.sort(byCountDesc);
    backlogList.sort(byCountDesc);
    return { backlog: backlogList, known: knownList };
  }, [stats]);

  const knownPlayTotal = useMemo(
    () => known.reduce((acc, s) => acc + s.count, 0),
    [known],
  );

  return (
    <div>
      <h2 style={styles.pageTitle}>SFX</h2>
      <p style={styles.pageSub}>
        Sound tags the AI has emitted, ordered by frequency. The Backlog
        table is the curation work queue — tags here have been heard in
        the wild but aren't bundled yet.
      </p>

      {!loaded && loading ? (
        <div style={styles.empty}>Loading…</div>
      ) : error ? (
        <div style={{ ...styles.empty, color: tokens.destructive }}>{error}</div>
      ) : stats.length === 0 ? (
        <div style={emptyShellStyle}>
          No SFX activity yet — once the AI starts emitting sound tags,
          they'll appear here. Run some turns in any scenario to seed this
          list.
        </div>
      ) : (
        <>
          <SfxTable
            title={`Backlog — ${backlog.length} ${backlog.length === 1 ? "tag" : "tags"} not yet curated`}
            subtitle="Headline list. Source these in catalogue order — high count first."
            rows={backlog}
            emphasis="high"
            showRedDot={backlog.length > 0}
          />
          <div style={{ height: 28 }} />
          <SfxTable
            title={`Known tags — ${known.length} ${known.length === 1 ? "tag" : "tags"} in catalogue, ${formatNumber(knownPlayTotal)} ${knownPlayTotal === 1 ? "play" : "plays"}`}
            subtitle="Confirms what's playing in the wild."
            rows={known}
            emphasis="low"
            showRedDot={false}
          />
        </>
      )}

      <p style={footerNoteStyle}>
        Working list of every planned tag lives in{" "}
        <code style={inlineCodeStyle}>web/docs/AUDIO_CATALOGUE.md</code>.
      </p>
    </div>
  );
}

// ── SfxTable ───────────────────────────────────────────────────────────

interface SfxTableProps {
  title: string;
  subtitle: string;
  rows: AdminSfxTagStat[];
  emphasis: "high" | "low";
  showRedDot: boolean;
}

function SfxTable({ title, subtitle, rows, emphasis, showRedDot }: SfxTableProps) {
  const [expandedTag, setExpandedTag] = useState<string | null>(null);

  const headerTitleStyle: CSSProperties =
    emphasis === "high" ? tableTitleHighStyle : tableTitleLowStyle;
  const wrapperStyle: CSSProperties =
    emphasis === "high"
      ? styles.tableWrap
      : { ...styles.tableWrap, opacity: 0.85 };

  return (
    <section>
      <header style={tableHeaderStyle}>
        <div>
          <h3 style={headerTitleStyle}>
            {showRedDot && <span style={redDotStyle} aria-hidden="true" />}
            {title}
          </h3>
          <div style={tableSubtitleStyle}>{subtitle}</div>
        </div>
      </header>

      {rows.length === 0 ? (
        <div style={emptyShellStyle}>Nothing here yet.</div>
      ) : (
        <div style={wrapperStyle}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, width: 28 }} aria-label="Expand" />
                <th style={styles.th}>Tag</th>
                <th style={{ ...styles.th, width: 120 }}>Category</th>
                <th
                  style={{ ...styles.th, width: 80, textAlign: "right" }}
                >
                  Count
                </th>
                <th style={{ ...styles.th, width: 220 }}>Top scenario</th>
                <th style={{ ...styles.th, width: 140 }}>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <SfxRow
                  key={row.tag}
                  row={row}
                  expanded={expandedTag === row.tag}
                  onToggle={() =>
                    setExpandedTag(expandedTag === row.tag ? null : row.tag)
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ── SfxRow ─────────────────────────────────────────────────────────────

interface SfxRowProps {
  row: AdminSfxTagStat;
  expanded: boolean;
  onToggle: () => void;
}

function SfxRow({ row, expanded, onToggle }: SfxRowProps) {
  const top = useMemo(() => describeTopScenario(row.perScenarioCounts), [
    row.perScenarioCounts,
  ]);
  const sortedBreakdown = useMemo(() => {
    return Object.entries(row.perScenarioCounts)
      .map(([scenarioId, count]) => ({ scenarioId, count }))
      .sort((a, b) => b.count - a.count);
  }, [row.perScenarioCounts]);

  return (
    <>
      <tr style={{ cursor: "pointer" }} onClick={onToggle}>
        <td style={{ ...styles.td, ...styles.tdMono, color: tokens.inkDim }}>
          {expanded ? "▾" : "▸"}
        </td>
        <td style={{ ...styles.td, ...styles.tdMono }}>{row.tag}</td>
        <td style={styles.td}>
          <CategoryChip category={row.category} />
        </td>
        <td
          style={{
            ...styles.td,
            ...styles.tdMono,
            textAlign: "right",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {formatNumber(row.count)}
        </td>
        <td style={styles.td}>
          <span style={topScenarioLabelStyle}>{top.label}</span>
          {top.extra && (
            <span style={topScenarioExtraStyle}>{top.extra}</span>
          )}
        </td>
        <td style={{ ...styles.td, color: tokens.inkMuted, fontSize: 12 }}>
          {formatRelative(row.lastSeenIso)}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} style={expandedCellStyle}>
            <div style={expandedShellStyle}>
              <div style={breakdownHeaderStyle}>Per-scenario breakdown</div>
              {sortedBreakdown.length === 0 ? (
                <div style={{ color: tokens.inkMuted, fontSize: 13 }}>
                  No per-scenario data yet.
                </div>
              ) : (
                <table style={breakdownTableStyle}>
                  <thead>
                    <tr>
                      <th style={breakdownThStyle}>Scenario</th>
                      <th
                        style={{
                          ...breakdownThStyle,
                          textAlign: "right",
                          width: 100,
                        }}
                      >
                        Plays
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedBreakdown.map((b) => (
                      <tr key={b.scenarioId}>
                        <td style={breakdownTdStyle}>
                          {scenarioLabel(b.scenarioId)}
                        </td>
                        <td
                          style={{
                            ...breakdownTdStyle,
                            ...styles.tdMono,
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {formatNumber(b.count)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────

function isTagKnown(tag: string, category: SfxCategory): boolean {
  if (category === "ambient") {
    return Object.prototype.hasOwnProperty.call(AMBIENT_CATALOGUE, tag);
  }
  if (category === "sporadic") {
    return Object.prototype.hasOwnProperty.call(SPORADIC_CATALOGUE, tag);
  }
  return Object.prototype.hasOwnProperty.call(ONESHOT_CATALOGUE, tag);
}

function scenarioLabel(scenarioId: string): string {
  const entry = SCENARIO_PRESENTATIONS[scenarioId];
  if (entry && typeof entry.pillLabel === "string" && entry.pillLabel.length > 0) {
    return entry.pillLabel;
  }
  return scenarioId;
}

/**
 * Describe the top scenario in "Salt-Glass Tower" / "Salt-Glass Tower +2"
 * style — show the leader, then a "+N more" suffix when there are other
 * scenarios with meaningful counts (>= 25% of the leader's count or any
 * tied entries).
 */
function describeTopScenario(
  perScenarioCounts: Record<string, number>,
): { label: string; extra: string | null } {
  const entries = Object.entries(perScenarioCounts)
    .map(([scenarioId, count]) => ({ scenarioId, count }))
    .sort((a, b) => b.count - a.count);
  if (entries.length === 0) return { label: "—", extra: null };
  const top = entries[0]!;
  const others = entries.slice(1);
  const closeOnes = others.filter(
    (e) => e.count === top.count || e.count >= top.count * 0.25,
  );
  return {
    label: scenarioLabel(top.scenarioId),
    extra: closeOnes.length > 0 ? ` + ${closeOnes.length} more` : null,
  };
}

function formatNumber(n: number): string {
  try {
    return new Intl.NumberFormat("en-US").format(n);
  } catch {
    return String(n);
  }
}

/**
 * Relative "Xm ago" / "Xh ago" / "yesterday" / ISO date formatter. Returns
 * "—" if the ISO is empty or unparseable.
 */
function formatRelative(iso: string): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "—";
  const deltaMs = Date.now() - t;
  if (deltaMs < 0) return "just now";
  const sec = Math.floor(deltaMs / 1000);
  if (sec < 5) return "just now";
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days === 1) return "yesterday";
  if (days < 7) return `${days}d ago`;
  // For older entries fall back to an ISO date — easier to scan than
  // "23d ago".
  return iso.slice(0, 10);
}

// ── CategoryChip ───────────────────────────────────────────────────────

function CategoryChip({ category }: { category: SfxCategory }) {
  let label: string;
  let chipStyle: CSSProperties = styles.pillGrey;
  if (category === "ambient") {
    label = "Ambient";
    chipStyle = styles.pillTrial;
  } else if (category === "sporadic") {
    label = "Sporadic";
    chipStyle = styles.pillWarn;
  } else {
    label = "One-shot";
    chipStyle = styles.pillActive;
  }
  return <span style={{ ...styles.pill, ...chipStyle }}>{label}</span>;
}

// ── Styles ─────────────────────────────────────────────────────────────

const tableHeaderStyle: CSSProperties = {
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "space-between",
  marginBottom: 10,
  gap: 16,
};

const tableTitleHighStyle: CSSProperties = {
  fontSize: 16,
  fontWeight: 600,
  color: tokens.inkPrimary,
  margin: "0 0 2px",
  display: "flex",
  alignItems: "center",
  gap: 8,
  letterSpacing: "-0.005em",
};

const tableTitleLowStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: tokens.inkMuted,
  margin: "0 0 2px",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
};

const tableSubtitleStyle: CSSProperties = {
  fontSize: 12,
  color: tokens.inkMuted,
};

const redDotStyle: CSSProperties = {
  display: "inline-block",
  width: 8,
  height: 8,
  borderRadius: "50%",
  background: tokens.destructive,
  boxShadow: `0 0 0 2px ${tokens.destructiveMuted}`,
};

const emptyShellStyle: CSSProperties = {
  background: tokens.panel,
  border: `1px solid ${tokens.border}`,
  borderRadius: 6,
  padding: "24px 20px",
  color: tokens.inkMuted,
  fontSize: 13,
  textAlign: "center",
};

const expandedCellStyle: CSSProperties = {
  padding: 0,
  background: tokens.panelAlt,
  borderBottom: `1px solid ${tokens.border}`,
};

const expandedShellStyle: CSSProperties = {
  padding: "14px 18px 18px",
  display: "flex",
  flexDirection: "column",
  gap: 8,
};

const breakdownHeaderStyle: CSSProperties = {
  fontSize: 11,
  color: tokens.inkMuted,
  textTransform: "uppercase",
  letterSpacing: "0.1em",
};

const breakdownTableStyle: CSSProperties = {
  width: "100%",
  maxWidth: 460,
  borderCollapse: "collapse",
  fontSize: 13,
};

const breakdownThStyle: CSSProperties = {
  textAlign: "left",
  padding: "6px 10px",
  fontWeight: 600,
  color: tokens.inkMuted,
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  borderBottom: `1px solid ${tokens.border}`,
};

const breakdownTdStyle: CSSProperties = {
  padding: "6px 10px",
  borderBottom: `1px solid ${tokens.border}`,
  color: tokens.inkPrimary,
};

const topScenarioLabelStyle: CSSProperties = {
  color: tokens.inkPrimary,
  fontSize: 13,
};

const topScenarioExtraStyle: CSSProperties = {
  color: tokens.inkDim,
  fontSize: 12,
};

const footerNoteStyle: CSSProperties = {
  marginTop: 24,
  fontSize: 12,
  color: tokens.inkMuted,
};

const inlineCodeStyle: CSSProperties = {
  fontFamily: tokens.fontMono,
  fontSize: 12,
  background: tokens.panelAlt,
  padding: "1px 6px",
  borderRadius: 3,
  border: `1px solid ${tokens.border}`,
  color: tokens.inkPrimary,
};
